/* ── Embed Analytics — the numbers behind the Embed section ────────────
   Built from data the analytics page already holds, the same way Share
   Analytics is: the agency's VR sessions, its customers, and the embed keys
   (one per asset per website).

   What each number means:
     Opens     — VR sessions that carry an embed_key: a visitor pressed play on
                 a digital-twin embed. Meta-model embeds don't produce these
                 yet: the 3D engine starts its own session inside its iframe
                 and never learns the key. Their LEADS are counted, because the
                 lead gate sends the key itself.
     Visitors  — distinct browsers across those opens (visitor_id, else the
                 lead, else the session).
     Leads     — customers created through an embed (embed_key, or origin
                 source embed / sdk).

   Kept pure so the page stays a renderer and this stays testable. */

export interface EmbedKeyInfo {
  key: string;
  project_id?: string;
  /** Which allowlist entry this placement is bound to. Needed to spot a deleted website. */
  site_id?: string | null;
  experience_kind?: string;
  experience_id?: string;
  site_host?: string | null;
  label?: string | null;
  published?: boolean;
  status?: string;
  /** The server's verdict — the same one render_embed gives a visitor. */
  state?: EmbedPlacementState | null;
  live?: boolean | null;
}

/** What the backend's _placement_state answers, plus one this file adds.
 *
 *  "ended" is FRONTEND-ONLY and means: this row exists because of history —
 *  sessions or leads reference the key — but no current placement backs it. A
 *  revoked key is the usual cause (the backend leaves them out of the keys list
 *  unless asked), so we cannot name the reason, only that it isn't running. */
export type EmbedPlacementState =
  "live" | "unpublished" | "site_removed" | "revoked" | "ended";

export interface EmbedRow {
  key: string;
  domain: string;
  projectId: string;
  experienceKind: string;
  experienceId: string;
  label: string;
  live: boolean;
  /** Why it isn't live, when it isn't. */
  state: EmbedPlacementState;
  opens: number;
  visitors: number;
  totalSeconds: number;
  leads: number;
  lastOpenedAt: number | null;
}

export interface EmbedLead {
  id: string;
  name: string;
  mobile: string;
  domain: string;
  key: string;
  projectId: string;
  createdAt: number | null;
}

export interface EmbedSummary {
  websites: number;
  opens: number;
  visitors: number;
  leads: number;
  rows: EmbedRow[];
  recentLeads: EmbedLead[];
}

const EMBED_SOURCES = new Set(["embed", "sdk"]);

/* ── Did we actually load, or did we just fail quietly? ────────────────────
   An empty list and a failed request produce the same zero, and a zero on an
   analytics page reads as "you had no leads" rather than "we couldn't ask".
   That is a worse lie than an error message, so the section tracks which of the
   two it is and never prints a number it can't stand behind. */
export type EmbedLoadState = "loading" | "ready" | "error";

export const embedSectionState = (o: {
  active: boolean;
  /** The parent's own project fetch failed — we never even got to ask for keys. */
  projectsFailed?: boolean;
  projectCount: number;
  /** Projects whose key lookup threw. */
  failedCount: number;
  /** The key fetch finished (successfully or not). */
  done: boolean;
}): EmbedLoadState => {
  if (!o.active) return "loading";
  if (o.projectsFailed) return "error";
  if (!o.done) return "loading";
  // Every project failed: we know nothing, so claiming "no embeds" would be a guess.
  if (o.projectCount > 0 && o.failedCount >= o.projectCount) return "error";
  return "ready";
};

/**
 * The same verdict render_embed gives a visitor, worked out on the client.
 *
 * Used only when the server didn't send `state` (the backend that computes it isn't
 * deployed everywhere yet). The third question — is the website still on the allowlist —
 * is the one the old fallback couldn't ask, so an embed whose site had been deleted
 * showed as live while every visitor got a 404. Pass `liveSiteIds` and it can.
 */
export const derivePlacementState = (
  k: EmbedKeyInfo,
  liveSiteIds?: Set<string>,
): EmbedPlacementState => {
  if ((k.status || "active") === "revoked") return "revoked";
  // Only judge the site when we were actually given the allowlist: with no set at all,
  // "not in it" would condemn every placement.
  if (liveSiteIds && !liveSiteIds.has(String(k.site_id || ""))) return "site_removed";
  if (k.published === false) return "unpublished";
  return "live";
};

/** Backend timestamps come with and without a zone; zone-less ones are UTC. */
export const parseTs = (raw: unknown): number | null => {
  if (!raw) return null;
  const s = String(raw);
  const iso = /Z|[+-]\d{2}:?\d{2}$/.test(s) ? s : s.replace(" ", "T") + "Z";
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

export const isEmbedCustomer = (c: any): boolean =>
  !!c?.embed_key || EMBED_SOURCES.has(String(c?.origin?.source || ""));

const inRange = (t: number | null, startMs: number, endMs: number) => {
  if (startMs === -Infinity && endMs === Infinity) return true;
  return t != null && t >= startMs && t <= endMs;
};

export const summarizeEmbeds = (o: {
  sessions: any[];
  customers: any[];
  keys: EmbedKeyInfo[];
  /** site_ids still on an allowlist. Omit it and the site check is skipped. */
  liveSiteIds?: Set<string>;
  startMs?: number;
  endMs?: number;
  projectId?: string;
}): EmbedSummary => {
  const startMs = o.startMs ?? -Infinity;
  const endMs = o.endMs ?? Infinity;
  const rows = new Map<string, EmbedRow & { visitorSet: Set<string> }>();

  const rowFor = (key: string, seed: { domain?: string | null; projectId?: string | null; kind?: string; expId?: string }) => {
    let r = rows.get(key);
    if (!r) {
      r = {
        key,
        domain: seed.domain || "",
        projectId: seed.projectId || "",
        experienceKind: seed.kind || "",
        experienceId: seed.expId || "",
        label: "",
        live: false,
        // NOT "live". A row is created by whichever loop mentions its key first, and
        // only the keys loop sets state — so for a placement that no longer exists,
        // the row is born here and nothing ever corrects it. "live" as a placeholder
        // meant a revoked embed with traffic rendered with no badge at all, which is
        // exactly what a working one looks like.
        state: "ended",
        opens: 0,
        visitors: 0,
        totalSeconds: 0,
        leads: 0,
        lastOpenedAt: null,
        visitorSet: new Set(),
      };
      rows.set(key, r);
    }
    if (!r.domain && seed.domain) r.domain = seed.domain;
    if (!r.projectId && seed.projectId) r.projectId = seed.projectId;
    if (!r.experienceKind && seed.kind) r.experienceKind = seed.kind;
    if (!r.experienceId && seed.expId) r.experienceId = seed.expId;
    return r;
  };

  /** Rows whose state came from a real placement record — everything else is history-only. */
  const fromKey = new Set<string>();
  /** Revoked keys don't get a row of their own, but they can NAME a history row's state. */
  const revoked = new Set<string>();

  // Keys first, so a published embed nobody has opened still shows as a row.
  for (const k of o.keys || []) {
    if (!k?.key) continue;
    // A revoked placement must not CREATE a row — an embed that was thrown away without
    // ever being opened is noise. But if history already put a row here, it is the only
    // thing that can say why that row is dead, so remember it for the pass below.
    if (k.status === "revoked" || k.state === "revoked") { revoked.add(k.key); continue; }
    fromKey.add(k.key);
    const r = rowFor(k.key, {
      domain: k.site_host,
      projectId: k.project_id,
      kind: k.experience_kind,
      expId: k.experience_id,
    });
    r.label = k.label || "";
    // Trust the server's verdict. Deriving it here from `published` + `status` missed the
    // third question render_embed asks — is the website still on the allowlist — so an embed
    // whose site had been deleted showed as live while every visitor got a 404.
    r.state = (k.state as EmbedPlacementState) || derivePlacementState(k, o.liveSiteIds);
    r.live = k.live != null ? !!k.live : r.state === "live";
  }

  for (const s of o.sessions || []) {
    if (!s?.embed_key) continue;
    const t = parseTs(s.timestamp);
    if (!inRange(t, startMs, endMs)) continue;
    const r = rowFor(String(s.embed_key), {
      domain: s.embed_domain,
      projectId: s.project_id,
      kind: s.meta_model_id ? "meta_model" : s.matterport_id ? "digital_twin" : "",
      expId: s.meta_model_id || s.matterport_id,
    });
    r.opens += 1;
    r.visitorSet.add(String(s.visitor_id || (s.customer_id ? `c:${s.customer_id}` : s._id || s.session_id)));
    if (typeof s.total_duration === "number") r.totalSeconds += s.total_duration;
    if (t != null && (r.lastOpenedAt == null || t > r.lastOpenedAt)) r.lastOpenedAt = t;
  }

  const recentLeads: EmbedLead[] = [];
  for (const c of o.customers || []) {
    if (!isEmbedCustomer(c)) continue;
    const t = parseTs(c.created_at);
    if (!inRange(t, startMs, endMs)) continue;
    const key = String(c.embed_key || "");
    const r = key ? rowFor(key, { domain: c.embed_domain }) : null;
    if (r) r.leads += 1;
    recentLeads.push({
      id: String(c._id || c.id || ""),
      name: [c.first_name, c.last_name].filter(Boolean).join(" ").trim() || "—",
      mobile: String(c.mobile_number || ""),
      domain: String(c.embed_domain || r?.domain || ""),
      key,
      projectId: r?.projectId || "",
      createdAt: t,
    });
  }

  /* Rows the keys loop never reached: created by a session or a lead, for a placement
     that is no longer in the keys list. They keep every number they earned — the history
     is the point — but they must not claim to be running. Say "revoked" when the server
     told us that much, otherwise only that it has ended. */
  for (const r of rows.values()) {
    if (fromKey.has(r.key)) continue;
    r.state = revoked.has(r.key) ? "revoked" : "ended";
    r.live = false;
  }

  let list = Array.from(rows.values());
  let leads = recentLeads;
  if (o.projectId) {
    list = list.filter((r) => r.projectId === o.projectId);
    const keep = new Set(list.map((r) => r.key));
    leads = leads.filter((l) => keep.has(l.key));
  }

  const allVisitors = new Set<string>();
  const out: EmbedRow[] = list.map(({ visitorSet, ...r }) => {
    visitorSet.forEach((v) => allVisitors.add(v));
    return { ...r, visitors: visitorSet.size };
  });
  out.sort((a, b) => b.opens - a.opens || b.leads - a.leads || a.domain.localeCompare(b.domain));
  leads.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  return {
    websites: new Set(out.map((r) => r.domain).filter(Boolean)).size,
    opens: out.reduce((n, r) => n + r.opens, 0),
    visitors: allVisitors.size,
    leads: leads.length,
    rows: out,
    recentLeads: leads,
  };
};
